import { COLOR_ORDER, FACE_ORDER } from './cube-config.js';
import { STICKERS_PER_FACE_4X4, assertStickerIndex4x4 } from './cube-config-4x4.js';
import { defaultOrientation4x4, deriveOrientation4x4 } from './orientation-4x4.js';

const SERIALIZATION_VERSION = 1;

function cloneStickers(stickers) {
  return Object.fromEntries(FACE_ORDER.map((face) => [face, [...stickers[face]]]));
}

function emptyStickers() {
  return Object.fromEntries(FACE_ORDER.map((face) => [face, Array(STICKERS_PER_FACE_4X4).fill(null)]));
}

function validateStickerData(stickers) {
  if (!stickers || typeof stickers !== 'object') throw new TypeError('4×4 贴纸数据缺失');
  for (const face of FACE_ORDER) {
    if (!Array.isArray(stickers[face]) || stickers[face].length !== STICKERS_PER_FACE_4X4) {
      throw new TypeError(`${face} 面必须包含 16 个贴纸`);
    }
    for (const color of stickers[face]) {
      if (color !== null && !COLOR_ORDER.includes(color)) throw new TypeError(`${face} 面包含未知颜色：${color}`);
    }
  }
}

export class CubeState4x4 {
  #history = [];

  constructor({ stickers = null, orientation = defaultOrientation4x4() } = {}) {
    this.order = 4;
    this.orientation = orientation.faceToColor ? orientation : deriveOrientation4x4(orientation);
    this.stickers = stickers ? cloneStickers(stickers) : emptyStickers();
    validateStickerData(this.stickers);
  }

  static solved(orientation = defaultOrientation4x4()) {
    const derived = orientation.faceToColor ? orientation : deriveOrientation4x4(orientation);
    const stickers = Object.fromEntries(FACE_ORDER.map((face) => (
      [face, Array(STICKERS_PER_FACE_4X4).fill(derived.faceToColor[face])]
    )));
    return new CubeState4x4({ stickers, orientation: derived });
  }

  static deserialize(serialized, orientation = defaultOrientation4x4()) {
    const data = typeof serialized === 'string' ? JSON.parse(serialized) : serialized;
    if (!data || data.version !== SERIALIZATION_VERSION || data.order !== 4) {
      throw new TypeError('4×4 录入数据版本不受支持');
    }
    if (data.faceOrder?.join(',') !== FACE_ORDER.join(',')) throw new TypeError('4×4 录入数据的面顺序不匹配');
    validateStickerData(data.stickers);
    return new CubeState4x4({ stickers: data.stickers, orientation });
  }

  isLocked() {
    return false;
  }

  get(face, index) {
    if (!FACE_ORDER.includes(face)) throw new RangeError(`未知魔方面：${face}`);
    assertStickerIndex4x4(index);
    return this.stickers[face][index];
  }

  paint(face, index, color) {
    if (!FACE_ORDER.includes(face)) throw new RangeError(`未知魔方面：${face}`);
    assertStickerIndex4x4(index);
    if (!COLOR_ORDER.includes(color)) throw new TypeError(`未知颜色：${color}`);
    const previous = this.stickers[face][index];
    if (previous === color) return { changed: false, reason: 'unchanged' };
    if (this.countColors()[color] >= STICKERS_PER_FACE_4X4) return { changed: false, reason: 'color-full' };
    this.stickers[face][index] = color;
    this.#history.push({ type: 'paint', face, index, previous });
    return { changed: true };
  }

  undo() {
    const operation = this.#history.pop();
    if (!operation) return false;
    if (operation.type === 'paint') this.stickers[operation.face][operation.index] = operation.previous;
    else if (operation.type === 'reset') this.stickers = cloneStickers(operation.previous);
    return true;
  }

  reset() {
    if (this.filledCount === 0) return false;
    const previous = cloneStickers(this.stickers);
    this.stickers = emptyStickers();
    this.#history.push({ type: 'reset', previous });
    return true;
  }

  countColors() {
    const counts = Object.fromEntries(COLOR_ORDER.map((color) => [color, 0]));
    for (const face of FACE_ORDER) {
      for (const color of this.stickers[face]) if (color !== null) counts[color] += 1;
    }
    return counts;
  }

  get filledCount() {
    return Object.values(this.countColors()).reduce((sum, count) => sum + count, 0);
  }

  get canUndo() {
    return this.#history.length > 0;
  }

  serialize() {
    return JSON.stringify({
      version: SERIALIZATION_VERSION,
      order: 4,
      faceOrder: FACE_ORDER,
      stickers: cloneStickers(this.stickers),
    });
  }
}

